// src/lib/opportunity/opportunityFetch.ts
//
// Domain-specific fetch wrapper for the Opportunity Engine — mirror of
// `manufacturingFetch` / `opsFetch`. Centralizes the
//   Authorization: Bearer <jwt> + X-Workspace-Organization-Id
// header pattern so every opportunity client function gets identical
// auth + workspace-org scoping without re-deriving it.

import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";

export type OpportunityFetchResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; payload?: Record<string, unknown> };

export async function opportunityFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<OpportunityFetchResult<T>> {
  if (!supabase) {
    return { ok: false, status: 503, error: "App is not configured for sign-in." };
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    return { ok: false, status: 401, error: "Sign in required." };
  }

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    ...(init.headers || {}),
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) (headers as Record<string, string>)["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(path, { ...init, headers });
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text) as T;
  } catch {
    // non-JSON
  }
  if (!r.ok) {
    const err =
      json && typeof json === "object" && json !== null && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : text || `HTTP ${r.status}`;
    return {
      ok: false,
      status: r.status,
      error: err,
      payload:
        json && typeof json === "object" && json !== null
          ? (json as Record<string, unknown>)
          : undefined,
    };
  }
  return { ok: true, status: r.status, data: json as T };
}
